import React, { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { observer } from 'mobx-react';
import { rgba } from 'polished';
import { TableCell, TableRow } from '@this/src/components/shared/ui/data_displays/table';
import type HotelOrderItemEanTodo from '@this/src/domain/arrangement/hotel_order_item_ean_todo';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Fetcher } from '@this/src/util';
import LogModal from '../../../log_modal';

interface Props {
  todo: HotelOrderItemEanTodo;
  checked: boolean;
  showDescription: boolean;
  onInventory: () => void;
  onToggle: (checked: boolean) => void;
}

const ItemsTableRow = observer(({ todo, checked, showDescription, onInventory, onToggle }: Props) => {
  const [showLogModal, setShowLogModal] = useState<boolean>(false);
  const styles = useMemo(() => getStyles(), []);

  const handleInventory = async () => {
    await Fetcher.put(`/arrangement/auto_reservation_inventory/ean_hotel/items/${todo.orderItemId}/inventory`, {});
    onInventory();
  };

  const handleCancelInventory = async () => {
    await Fetcher.put(
      `/arrangement/auto_reservation_inventory/ean_hotel/items/${todo.orderItemId}/cancel_inventory`,
      {}
    );
    onInventory();
  };

  return (
    <>
      <TableRow style={{ background: todo.isDiff || !todo.orderItemId ? rgba('red', 0.3) : 'auto' }}>
        <TableCell rowSpan={2}>
          <Checkbox
            checked={checked}
            disabled={!todo.orderItemId || Boolean(todo.inventoriedAt)}
            onChange={e => onToggle(e.target.checked)}
          />
        </TableCell>
        <TableCell>
          <span style={styles.label}>OrderItem</span>
        </TableCell>
        <TableCell>{todo.fromOrderItem?.reservationNumber || '─'}</TableCell>
        <TableCell>{todo.fromOrderItem?.peopleNum ? `${todo.fromOrderItem?.peopleNum}人` : '─'}</TableCell>
        <TableCell>{todo.fromOrderItem?.hotelName || '─'}</TableCell>
        <TableCell>{todo.fromOrderItem?.hotelAddress || '─'}</TableCell>
        <TableCell>{todo.fromOrderItem?.roomType || '─'}</TableCell>
        <TableCell>{todo.fromOrderItem?.planName || '─'}</TableCell>
        <TableCell>{todo.showMultipleHotelPlans ? '○' : ''}</TableCell>
        <TableCell>{todo.userCheckerStr()}</TableCell>
        <TableCell>
          {todo.fromOrderItem?.breakfast === null ? '─' : todo.fromOrderItem.breakfast ? 'あり' : 'なし'}
        </TableCell>
        <TableCell>{todo.fromOrderItem?.checkinDate || '─'}</TableCell>
        <TableCell>{todo.fromOrderItem?.checkoutDate || '─'}</TableCell>
        <TableCell>{todo.fromOrderItem?.travelerName || '─'}</TableCell>
        <TableCell rowSpan={2}>
          <div>代金合計: {todo.price?.toLocaleString()}円</div>
          <div>卸値: {todo.originalPrice?.toLocaleString()}円</div>
          <div>EAN側料金: {todo.hotelPrice?.toLocaleString()}円</div>
        </TableCell>
        <TableCell rowSpan={2}>{todo.suppliedItemName || '─'}</TableCell>
        <TableCell rowSpan={2}>{todo.isInventoried ? '済' : '未'}</TableCell>
        <TableCell rowSpan={2}>{todo.inventoriedBy}</TableCell>
        <TableCell rowSpan={2}>{todo.inventoriedAt}</TableCell>
        <TableCell rowSpan={2} nowrap={!showDescription}>
          <Link
            href={`/arrangement/virtual_counter?trip_id=${todo.tripId}`}
            target="_blank"
            rel="noopener noreffer"
          >
            {todo.tripId}
          </Link>
        </TableCell>
        <TableCell rowSpan={2} nowrap={!showDescription}>
          {todo.traceId}
        </TableCell>
        <TableCell rowSpan={2}>
          {todo.specialCheckinInstructions && (
            <div style={styles.specialInstructions}>{todo.specialCheckinInstructions}</div>
          )}
        </TableCell>
        <TableCell rowSpan={2}>
          {todo.lastLogMessage && <span>{todo.lastLogMessage}</span>}
          {todo.lastLogMessageArranger && <span>({todo.lastLogMessageArranger})</span>}
          {todo.orderItemId && (
            <p>
              (<Link onClick={() => setShowLogModal(true)}>ログ</Link>)
            </p>
          )}
        </TableCell>
        <TableCell rowSpan={2}>
          {todo.eanPhase === 'api_error' && <span style={styles.errorBadge}>API エラー</span>}
          {todo.orderItemId &&
            (todo.inventoriedAt ? (
              <Button size="small" onClick={() => handleCancelInventory()}>
                取消
              </Button>
            ) : (
              <Button size="small" onClick={() => handleInventory()}>
                棚卸完了
              </Button>
            ))}
        </TableCell>
        {showLogModal && todo.traceId && (
          <LogModal traceId={todo.traceId} onClose={() => setShowLogModal(false)} />
        )}
      </TableRow>
      <TableRow style={{ background: Object.keys(todo.diff).length > 0 ? rgba('red', 0.3) : 'auto' }}>
        <TableCell>
          <span style={styles.label}>EAN</span>
        </TableCell>
        <TableCell>{todo.fromEanRecord?.reservationNumber || '─'}</TableCell>
        <TableCell>{todo.fromEanRecord?.peopleNum ? `${todo.fromEanRecord?.peopleNum}人` : '─'}</TableCell>
        <TableCell>{todo.fromEanRecord?.hotelName || '─'}</TableCell>
        <TableCell>{todo.fromEanRecord?.hotelAddress || '─'}</TableCell>
        <TableCell>{todo.fromEanRecord?.roomType || '─'}</TableCell>
        <TableCell>{todo.fromEanRecord?.planName || '─'}</TableCell>
        <TableCell>{todo.showMultipleHotelPlans ? '○' : ''}</TableCell>
        <TableCell />
        <TableCell>
          {todo.fromEanRecord?.breakfast === null ? '─' : todo.fromEanRecord.breakfast ? 'あり' : 'なし'}
        </TableCell>
        <TableCell>{todo.fromEanRecord?.checkinDate || '─'}</TableCell>
        <TableCell>{todo.fromEanRecord?.checkoutDate || '─'}</TableCell>
        <TableCell>{todo.fromEanRecord?.travelerName || '─'}</TableCell>
      </TableRow>
    </>
  );
});

const getStyles = () => ({
  label: {
    fontSize: '0.8em',
    fontWeight: 'bold'
  } as CSSProperties,
  specialInstructions: {
    fontSize: '0.85em',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  } as CSSProperties,
  errorBadge: {
    display: 'inline-block',
    background: '#a72c09',
    color: 'white',
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 3,
    marginBottom: 4
  } as CSSProperties
});

export default ItemsTableRow;
